import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { getAnalytics, isSupported } from "firebase/analytics";

import { config } from "./config";

/**
 * Firebase serves the OAuth handler page and its companion iframe from
 * `authDomain`. Pointed at `<project>.firebaseapp.com` that origin is
 * third-party to us, and handing the credential back to the app depends on
 * storage the browser is free to partition. Mobile browsers with cross-site
 * tracking prevention on (Safari's default) partition it, which strands the
 * user on a blank handler page that never returns.
 *
 * So we point `authDomain` at the app's own host instead, making the handler
 * and iframe first-party. Read off `window.location` so local, staging and
 * production each use their own domain with nothing hardcoded.
 *
 * Every https deployment of this app therefore owes two things, or Google and
 * Apple sign-in will fail outright on it:
 *   1. a transparent (non-redirecting) proxy from `/__/auth/**` on its own host
 *      through to `REACT_APP_FIREBASE_AUTH_DOMAIN`
 *   2. that host added to Firebase's authorized domains, and
 *      `https://<host>/__/auth/handler` added to the Apple Service ID's Return
 *      URLs
 */
const sameOriginAuth =
  typeof window !== "undefined" &&
  // Firebase always builds the handler URL as https, so an http dev server
  // can't serve it. Local dev falls back to the Firebase host, where popup
  // sign-in works fine on desktop.
  window.location.protocol === "https:";

const firebaseConfig = {
  apiKey: config.FIREBASE_API_KEY,
  authDomain: sameOriginAuth
    ? window.location.host
    : config.FIREBASE_AUTH_DOMAIN,
  projectId: config.FIREBASE_PROJECT_ID,
  storageBucket: config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: config.FIREBASE_MESSAGEING_SENDER_ID,
  appId: config.FIREBASE_APP_ID,
  measurementId: config.FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth();

// Firebase Analytics. Lazily resolved — `isSupported()` returns false in
// SSR / unsupported browsers / when cookies are blocked, so we tolerate
// `analytics` being null and skip events in those cases.
let analyticsInstance = null;
isSupported()
  .then((supported) => {
    if (supported) analyticsInstance = getAnalytics(firebaseApp);
  })
  .catch(() => {
    // Analytics not available — events become no-ops.
  });

export const getAnalyticsInstance = () => analyticsInstance;

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account ",
});

const appleProvider = new OAuthProvider("apple.com");

/**
 * Mobile browsers have no real popups. `signInWithPopup` opens the handler as a
 * full tab, so on the way back it has to pass the credential to whichever tab
 * opened it — and that handover is what fails on mobile. A redirect keeps the
 * whole flow in one tab and needs no handover at all.
 */
const isMobileBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ sends a desktop user agent, so touch points are the only tell.
  const isIpad = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return (
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    isIpad
  );
};

/**
 * Redirect only helps once the handler is same-origin. Against a third-party
 * `authDomain` its single channel for returning the credential is storage the
 * browser partitions, so `getRedirectResult()` resolves null and the user lands
 * back on the login page with no error shown. Popup is the lesser evil there —
 * its opener-based path still works in some browsers, which is why sign-in
 * currently succeeds for some mobile users and not others.
 */
const shouldUseRedirect = () => sameOriginAuth && isMobileBrowser();

// Recorded before handing off to the provider so that, once the browser comes
// back, the page can show a loader instead of flashing its login form while the
// redirect result resolves.
const PENDING_REDIRECT_KEY = "clubbie:pending-oauth-redirect";

const readPendingRedirect = () => {
  try {
    return window.sessionStorage.getItem(PENDING_REDIRECT_KEY);
  } catch (err) {
    // Private browsing modes can throw on sessionStorage access. Losing the
    // hint only costs us the loader; the sign-in itself still completes.
    return null;
  }
};

const writePendingRedirect = (method) => {
  try {
    window.sessionStorage.setItem(PENDING_REDIRECT_KEY, method);
  } catch (err) {
    // See readPendingRedirect.
  }
};

const clearPendingRedirect = () => {
  try {
    window.sessionStorage.removeItem(PENDING_REDIRECT_KEY);
  } catch (err) {
    // See readPendingRedirect.
  }
};

const startSignIn = async (provider, method) => {
  if (!shouldUseRedirect()) return signInWithPopup(auth, provider);

  writePendingRedirect(method);
  await signInWithRedirect(auth, provider);
  // The browser is already navigating away, so nothing past here runs. Callers
  // treat a null result as "handed off, resolve on the way back".
  return null;
};

export const signInWithGoogle = () => startSignIn(googleProvider, "google");
export const signInWithApple = () => startSignIn(appleProvider, "apple");

/**
 * Splits a Firebase `UserCredential` into the login method Clubbie's auth API
 * expects and the provider credential carrying the id token. Returns null for
 * anything that isn't a Google or Apple sign-in.
 */
export const extractOAuthCredential = (result) => {
  if (!result) return null;

  const providerId =
    result.providerId || result.user?.providerData?.[0]?.providerId;

  if (providerId === "google.com") {
    return {
      method: "google",
      credential: GoogleAuthProvider.credentialFromResult(result),
    };
  }
  if (providerId === "apple.com") {
    return {
      method: "apple",
      credential: OAuthProvider.credentialFromResult(result),
    };
  }
  return null;
};

/** The provider a redirect is in flight for, or null if none is. */
export const pendingOAuthRedirect = () => readPendingRedirect();

// `getRedirectResult` yields the credential once and null on every call after,
// so the promise is shared: a second caller — a remounted effect, or two pages
// mounting in sequence — gets the same answer rather than an empty one.
let redirectResultPromise = null;

/**
 * Resolves a redirect sign-in the browser has just returned from. Null when
 * this page load wasn't reached by coming back from a provider.
 */
export const consumeOAuthRedirect = () => {
  if (!redirectResultPromise) {
    redirectResultPromise = getRedirectResult(auth)
      .then(extractOAuthCredential)
      .finally(clearPendingRedirect);
  }
  return redirectResultPromise;
};
